import type { SyncOptions } from "@powersync/common"

export type PowerSyncRuntimePhase =
  | "connect"
  | "start-attachments"
  | "restart-attachments"
  | "stop"

type PowerSyncDatabaseLike<TConnector> = {
  connect: (connector: TConnector, options?: SyncOptions) => Promise<unknown>
  disconnect: () => Promise<unknown>
}

type AttachmentQueueLike = {
  startSync: () => Promise<unknown>
  stopSync: () => Promise<unknown>
}

/**
 * Owns PowerSync and attachment-queue lifecycle transitions.
 *
 * React auth/project effects can fire back-to-back. Serializing their work
 * prevents concurrent startSync/stopSync calls from disposing one another's
 * listeners and abort controllers.
 */
export class PowerSyncRuntime<TConnector> {
  private desired = false
  private connectionStarted = false
  private connectionAttempt = 0
  private attachmentsStarted = false
  private transition: Promise<void> = Promise.resolve()

  constructor(
    private readonly database: PowerSyncDatabaseLike<TConnector>,
    private readonly attachments: AttachmentQueueLike,
    private readonly connectorFactory: () => TConnector,
    private readonly connectionOptions: SyncOptions,
    private readonly onError: (
      error: unknown,
      phase: PowerSyncRuntimePhase,
    ) => void,
  ) {}

  start(): Promise<void> {
    this.desired = true
    return this.enqueue(async () => {
      if (!this.desired) return

      if (!this.connectionStarted) {
        this.connectionStarted = true
        const connectionAttempt = ++this.connectionAttempt
        try {
          // PowerSync's promise resolves on the first successful connection,
          // while its internal loop owns transient reconnect attempts. Do not
          // block attachment initialization on that potentially long wait.
          void this.database
            .connect(this.connectorFactory(), this.connectionOptions)
            .catch((error) => {
              if (connectionAttempt !== this.connectionAttempt) return
              this.connectionStarted = false
              // disconnect() rejects an in-flight connect promise during a
              // normal logout/unmount. Once stop() has cleared the desired
              // state, that rejection is expected and should not reach Sentry.
              if (this.desired) this.onError(error, "connect")
            })
        } catch (error) {
          this.connectionStarted = false
          this.onError(error, "connect")
          throw error
        }
      }

      if (!this.attachmentsStarted) {
        try {
          await this.attachments.startSync()
          this.attachmentsStarted = true
        } catch (error) {
          this.onError(error, "start-attachments")
          throw error
        }
      }
    })
  }

  restartAttachments(): Promise<void> {
    return this.enqueue(async () => {
      if (!this.desired || !this.connectionStarted) return
      try {
        // AttachmentQueue.startSync() stops an existing queue before starting
        // it, so one awaited call is the complete restart transition.
        await this.attachments.startSync()
        this.attachmentsStarted = true
      } catch (error) {
        this.attachmentsStarted = false
        this.onError(error, "restart-attachments")
        throw error
      }
    })
  }

  stop(): Promise<void> {
    this.desired = false
    return this.enqueue(async () => {
      if (!this.connectionStarted && !this.attachmentsStarted) return

      // Invalidate the pending connect handler before disconnecting so its
      // eventual rejection cannot clear or report against a later start().
      this.connectionAttempt += 1

      const failures: unknown[] = []
      try {
        await this.attachments.stopSync()
      } catch (error) {
        failures.push(error)
      } finally {
        this.attachmentsStarted = false
      }

      try {
        await this.database.disconnect()
      } catch (error) {
        failures.push(error)
      } finally {
        this.connectionStarted = false
      }

      if (failures.length > 0) {
        const error =
          failures.length === 1
            ? failures[0]
            : new AggregateError(failures, "Failed to stop PowerSync runtime")
        this.onError(error, "stop")
        throw error
      }
    })
  }

  private enqueue(operation: () => Promise<void>): Promise<void> {
    const next = this.transition.then(operation)
    // Keep the internal chain usable after a reported failure while returning
    // the original rejecting promise to the caller.
    this.transition = next.catch(() => undefined)
    return next
  }
}
